import {
  findFirstFieldDefaultValueById,
  type LinkedHopSegment,
} from './passportRefGridLinkedParse'

type HttpGet = {
  get: (url: string) => Promise<{ data?: unknown }>
}

/** Root/runtime field map: keys are field ids (strings). */
export type RootFieldValuesForRefDict = Record<string, string>

/** Cache/inflight key for object-type dictionary GETs (URL is scoped by group + object id). */
export function objectTypeDictionaryCacheKey(
  groupId: string,
  objectInstanceId: number,
): string {
  return `${groupId}:${objectInstanceId}`
}

/** Normalized row from `GET /inventory/group/{groupId}/object-types/{objectInstanceId}`. */
export type ObjectTypeDictionaryPayload = {
  object_type_id: number
  fields: Array<{ id: string | number; default_value?: unknown }>
  /** Optional schema ids (same positional order as `fields` when backend serializes values as repeated `key`). */
  schema_field_ids?: Array<string | number>
}

function parseLinkedObjectId(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === '') return null
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw > 0 ? raw : null
  const s = String(raw).trim()
  if (!s) return null
  const n = Number(s)
  if (Number.isFinite(n) && n > 0) return n
  return null
}

function readObjectTypeIdFromDictionaryBody(inner: Record<string, unknown>): number | null {
  if (typeof inner.object_type_id === 'number' && Number.isFinite(inner.object_type_id)) {
    return inner.object_type_id
  }
  const iot = inner.invent_object_type
  if (iot && typeof iot === 'object' && typeof (iot as { id?: unknown }).id === 'number') {
    return (iot as { id: number }).id
  }
  return null
}

/**
 * Maps an axios `{ data }` response to `{ object_type_id, fields }` for dictionary resolution.
 */
export function normalizeObjectTypeDictionaryPayload(res: {
  data?: unknown
}): ObjectTypeDictionaryPayload | null {
  const d = res.data
  if (d == null || typeof d !== 'object') return null
  const root = d as Record<string, unknown>
  const inner = (
    root.data && typeof root.data === 'object' ? root.data : root
  ) as Record<string, unknown>
  const fieldsRaw = inner.fields
  const fields = Array.isArray(fieldsRaw)
    ? (fieldsRaw as Array<{ id: string | number; default_value?: unknown }>)
    : []
  const schemaFromIot = (() => {
    const iot = inner.invent_object_type
    if (iot && typeof iot === 'object') {
      const fields = (iot as { fields?: unknown }).fields
      if (Array.isArray(fields)) {
        return fields
          .map((f) =>
            f && typeof f === 'object' && 'id' in (f as Record<string, unknown>)
              ? (f as { id?: string | number }).id
              : undefined,
          )
          .filter((id): id is string | number => id !== undefined && id !== null)
      }
    }
    return [] as Array<string | number>
  })()
  const schemaFromParentIot = (() => {
    const parentIot = inner.parent_invent_object_type
    if (parentIot && typeof parentIot === 'object') {
      const fields = (parentIot as { fields?: unknown }).fields
      if (Array.isArray(fields)) {
        return fields
          .map((f) =>
            f && typeof f === 'object' && 'id' in (f as Record<string, unknown>)
              ? (f as { id?: string | number }).id
              : undefined,
          )
          .filter((id): id is string | number => id !== undefined && id !== null)
      }
    }
    return [] as Array<string | number>
  })()
  const schemaFromParents = (() => {
    const parents = inner.parents
    if (!Array.isArray(parents)) return [] as Array<string | number>
    for (const p of parents) {
      if (!p || typeof p !== 'object') continue
      const fields = (p as { fields?: unknown }).fields
      if (!Array.isArray(fields)) continue
      const ids = fields
        .map((f) =>
          f && typeof f === 'object' && 'id' in (f as Record<string, unknown>)
            ? (f as { id?: string | number }).id
            : undefined,
        )
        .filter((id): id is string | number => id !== undefined && id !== null)
      if (ids.length > 0) return ids
    }
    return [] as Array<string | number>
  })()
  const schema_field_ids = (() => {
    const readIds = (src: unknown): Array<string | number> => {
      if (!Array.isArray(src)) return []
      return src
        .map((f) =>
          f && typeof f === 'object' && 'id' in (f as Record<string, unknown>)
            ? (f as { id?: string | number }).id
            : undefined,
        )
        .filter((id): id is string | number => id !== undefined && id !== null)
    }

    if (schemaFromIot.length > 0) return schemaFromIot
    if (schemaFromParentIot.length > 0) return schemaFromParentIot
    if (schemaFromParents.length > 0) return schemaFromParents
    const iot = inner.invent_object_type
    if (iot && typeof iot === 'object') return readIds((iot as { fields?: unknown }).fields)
    const parentIot = inner.parent_invent_object_type
    if (parentIot && typeof parentIot === 'object')
      return readIds((parentIot as { fields?: unknown }).fields)
    return []
  })()
  const object_type_id = readObjectTypeIdFromDictionaryBody(inner)
  if (object_type_id == null) return null
  return { object_type_id, fields, schema_field_ids }
}

function resolveFieldValueFromDictionary(
  payload: ObjectTypeDictionaryPayload,
  fieldId: string | number,
): unknown {
  const byId = findFirstFieldDefaultValueById(payload.fields, fieldId)
  if (byId !== undefined) return byId
  const schema = payload.schema_field_ids ?? []
  if (!schema.length || schema.length !== payload.fields.length) return undefined
  const idx = schema.findIndex((id) => String(id) === String(fieldId))
  if (idx < 0) return undefined
  return payload.fields[idx]?.default_value
}

async function fetchObjectTypeDictionaryPayload(
  http: HttpGet,
  groupId: string,
  objectInstanceId: number,
  cache: Map<string, ObjectTypeDictionaryPayload>,
  inflight: Map<string, Promise<ObjectTypeDictionaryPayload | null>>,
): Promise<ObjectTypeDictionaryPayload | null> {
  if (objectInstanceId <= 0 || !groupId) return null
  const key = objectTypeDictionaryCacheKey(groupId, objectInstanceId)
  const hit = cache.get(key)
  if (hit) return hit

  const pending = inflight.get(key)
  if (pending) return pending

  const p = (async () => {
    const res = await http.get(
      `/inventory/group/${groupId}/object-types/${objectInstanceId}`,
    )
    const norm = normalizeObjectTypeDictionaryPayload(res)
    if (!norm) {
      console.error(
        '[passport-ref-dict] invalid or missing object_type_id in object-types response',
        objectInstanceId,
      )
      return null
    }
    cache.set(key, norm)
    return norm
  })()

  inflight.set(key, p)
  try {
    return await p
  } finally {
    inflight.delete(key)
  }
}

/**
 * Resolves display text for `ref:[…]->[…]#f:…` grid ids using dictionary `object-types` loads (not passport rows).
 */
export async function resolveRefDictionaryDisplayChain(
  http: HttpGet,
  groupId: string,
  rootFieldValues: RootFieldValuesForRefDict,
  segments: LinkedHopSegment[],
  finalFieldId: string | number,
  cache: Map<string, ObjectTypeDictionaryPayload>,
  inflight: Map<string, Promise<ObjectTypeDictionaryPayload | null>>,
): Promise<string> {
  if (segments.length === 0) return ''
  const finalKey = String(finalFieldId)
  if (!finalKey) return ''

  const firstHopRaw = rootFieldValues[String(segments[0].field_id)]
  let currentObjectId = parseLinkedObjectId(
    firstHopRaw,
  )
  if (currentObjectId == null) return ''

  for (let i = 0; i < segments.length - 1; i++) {
    const payload = await fetchObjectTypeDictionaryPayload(
      http,
      groupId,
      currentObjectId,
      cache,
      inflight,
    )
    if (!payload) return ''
    const expected = segments[i].expected_object_type_id
    if (expected > 0 && payload.object_type_id !== expected) {
      console.error(
        `[passport-ref-dict] object_type_id mismatch: expected ${expected}, got ${payload.object_type_id} (hop ${i})`,
      )
    }
    const nextFieldId = segments[i + 1].field_id
    const nextVal = resolveFieldValueFromDictionary(payload, nextFieldId)
    currentObjectId = parseLinkedObjectId(nextVal)
    if (currentObjectId == null) {
      return ''
    }
  }

  const lastPayload = await fetchObjectTypeDictionaryPayload(
    http,
    groupId,
    currentObjectId,
    cache,
    inflight,
  )
  if (!lastPayload) return ''
  const lastExpected = segments[segments.length - 1].expected_object_type_id
  if (lastExpected > 0 && lastPayload.object_type_id !== lastExpected) {
    console.error(
      `[passport-ref-dict] object_type_id mismatch: expected ${lastExpected}, got ${lastPayload.object_type_id} (final)`,
    )
  }
  const raw = resolveFieldValueFromDictionary(lastPayload, finalFieldId)
  if (raw === undefined || raw === null || raw === '') return ''
  return String(raw)
}
